export async function apiFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    if (response.status === 401) {
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
      throw new Error('Unauthorized');
    }
    let message = 'API request failed';
    try {
      const payload = await response.json();
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        message = payload.error;
      } else if (typeof payload?.message === 'string' && payload.message.trim()) {
        message = payload.message;
      }
    } catch (_err) {
      // Ignore JSON parse failures and use fallback message.
    }
    throw new Error(message);
  }
  return response.json();
}

export async function fetchAuditLogs(start: string, end: string) {
  return apiFetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/reports/audit-logs?start=${start}&end=${end}`);
}

export async function fetchDonors(hotlistFilter: string) {
  let url = `${process.env.REACT_APP_BACKEND_API_URL}/api/donor`;
  if (hotlistFilter !== 'all') {
    url = `${process.env.REACT_APP_BACKEND_API_URL}/api/donor/hotlist?months=${hotlistFilter}`;
  }
  return apiFetch(url);
}

export async function downloadDonationsReport(start: string, end: string) {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/reports/donations?start=${start}&end=${end}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to download report');
  }
  return response.blob();
}

export interface DonationItemAnalyticsSummary {
  totalLineItems: number;
  totalQuantity: number;
  totalAmount: number;
  uniqueItems: number;
  uniqueDonors: number;
}

export interface DonationItemAnalyticsRow {
  itemId: string;
  itemName: string;
  pagePath: string;
  donationCount: number;
  totalQuantity: number;
  totalAmount: number;
  avgUnitAmount: number;
  uniqueDonors: number;
}

export interface DonationItemPageAnalyticsRow {
  pagePath: string;
  uniqueItems: number;
  totalQuantity: number;
  totalAmount: number;
}

export interface DonationItemContributionRow {
  donationId: number;
  date: string;
  donorName: string;
  donorPhone: string;
  donorEmail: string;
  itemId: string;
  itemName: string;
  pagePath: string;
  quantity: number;
  unitAmount: number;
  lineTotal: number;
  paymentMethod: string;
}

export interface DonationItemAnalyticsResponse {
  summary: DonationItemAnalyticsSummary;
  topItems: DonationItemAnalyticsRow[];
  byPage: DonationItemPageAnalyticsRow[];
  recentContributions: DonationItemContributionRow[];
}

export async function fetchDonationItemAnalytics(start: string, end: string) {
  return apiFetch<DonationItemAnalyticsResponse>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/reports/donation-items?start=${start}&end=${end}`,
  );
}

export async function fetchDonations() {
  return apiFetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation`);
}

export async function fetchOptions(type: string) {
  return apiFetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/options?type=${type}`);
}

export async function fetchDonationById(id: string) {
  return apiFetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donations/${id}`);
}

export async function fetchDonationForEdit(id: number | string) {
  return apiFetch<Record<string, any>>(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/${id}`);
}

export async function updateDonationById(id: number | string, payload: Record<string, any>) {
  return apiFetch<{ message: string }>(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export type NityaSevaPaymentFrequency = 'monthly' | 'quarterly' | 'half_yearly' | 'annually';

export interface NityaSevaEnrollment {
  id: number;
  userId: number;
  userName?: string;
  fullName: string;
  whatsAppNumber: string;
  phone?: string;
  email: string;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  dateOfBirth: string;
  anniversaryDate?: string;
  pledgeTier: string;
  pledgeAmount: number;
  paymentFrequency: NityaSevaPaymentFrequency;
  submissionMode: 'ecs_nach' | 'auto_razorpay' | 'online_transfer' | 'cash_first_sunday';
  panNumber?: string;
  status: string;
  enrollmentSource?: string;
  adminNote?: string;
  lastManualDonationAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NityaSevaDonationHistoryItem {
  kind?: 'paid' | 'due';
  month?: string;
  status?: string;
  displayDate?: string;
  amount?: number;
  paymentMethod?: string;
  canPayOnline?: boolean;
  dueReason?: string;
  donation?: {
    id: number;
    date: string;
    amount: number;
    paymentMethod?: string;
    status?: string;
    head?: string;
    purpose?: string;
    note?: string;
    createdAt?: string;
    txnRefId?: string;
    referenceId?: string;
  };
  subscription?: {
    id?: number;
    razorpayId?: string;
    type?: string;
    head?: string;
    status?: string;
    amount?: number;
    chargeAt?: string;
  };
}

export interface NityaSevaEnrollmentDetailResponse {
  enrollment: NityaSevaEnrollment;
  donationHistory: NityaSevaDonationHistoryItem[];
}

export interface NityaSevaMonthSummary {
  month: string;
  totalEnrollments: number;
  paidCount: number;
  dueCount: number;
  totalExpected: number;
  totalCollected: number;
  pendingAmount: number;
  manualCount: number;
  subscriptionCount: number;
}

export interface NityaSevaMonthDetailItem {
  enrollment: NityaSevaEnrollment;
  history: NityaSevaDonationHistoryItem;
  source: string;
}

export interface NityaSevaMonthDetailResponse {
  month: string;
  summary: NityaSevaMonthSummary;
  items: NityaSevaMonthDetailItem[];
}

export interface NityaSevaLookupCandidate {
  id: number;
  legalName: string;
  phone: string;
  whatsAppNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  panNumber: string;
  dateOfBirth: string;
  anniversaryDate?: string;
}

export interface NityaSevaSearchUserItem {
  id: number;
  legalName: string;
  phone: string;
  whatsAppNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  panNumber: string;
  dateOfBirth: string;
  anniversaryDate?: string;
  hasEnrollment: boolean;
  enrollmentId?: number;
  enrollmentStatus?: string;
}

export interface NityaSevaUserLookupResponse {
  found: boolean;
  ambiguous: boolean;
  matchedBy?: string;
  user?: NityaSevaLookupCandidate;
  candidates?: NityaSevaLookupCandidate[];
  existingEnrollment?: NityaSevaEnrollment;
}

export interface CreateOfflineNityaSevaEnrollmentPayload {
  userId?: number;
  createUserIfMissing: boolean;
  fullName: string;
  whatsAppNumber?: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  dateOfBirth?: string;
  anniversaryDate?: string;
  startDate?: string;
  pledgeTier: string;
  pledgeAmount: number;
  paymentFrequency: NityaSevaPaymentFrequency;
  submissionMode: NityaSevaEnrollment['submissionMode'];
  panNumber?: string;
  status?: string;
  adminNote?: string;
}

export interface CreateOfflineNityaSevaEnrollmentResponse {
  enrollment: NityaSevaEnrollment;
  user: NityaSevaLookupCandidate;
  userCreated: boolean;
}

export async function lookupNityaSevaUser(payload: { phone?: string; email?: string }) {
  return apiFetch<NityaSevaUserLookupResponse>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments/lookup-user`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function searchNityaSevaUsersByPhone(phone: string, limit = 8) {
  const search = new URLSearchParams();
  search.set('phone', phone);
  search.set('limit', String(limit));
  return apiFetch<NityaSevaSearchUserItem[]>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments/search-users?${search.toString()}`,
  );
}

export async function createOfflineNityaSevaEnrollment(payload: CreateOfflineNityaSevaEnrollmentPayload) {
  return apiFetch<CreateOfflineNityaSevaEnrollmentResponse>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchNityaSevaEnrollments(params?: {
  status?: string;
  mode?: string;
  q?: string;
  limit?: number;
  offset?: number;
}) {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.mode) search.set('mode', params.mode);
  if (params?.q) search.set('q', params.q);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const suffix = search.toString() ? `?${search.toString()}` : '';
  return apiFetch<NityaSevaEnrollment[]>(`${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments${suffix}`);
}

export async function updateNityaSevaEnrollment(
  id: number,
  payload: {
    fullName?: string;
    whatsAppNumber?: string;
    phone?: string;
    email?: string;
    address?: string;
    dateOfBirth?: string;
    anniversaryDate?: string;
    startDate?: string;
    pledgeAmount?: number;
    paymentFrequency?: NityaSevaPaymentFrequency;
    submissionMode?: NityaSevaEnrollment['submissionMode'];
    panNumber?: string;
    status?: string;
    adminNote?: string;
    lastManualDonationAt?: string;
  },
) {
  return apiFetch<{ enrollment: NityaSevaEnrollment }>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchNityaSevaEnrollmentDetail(id: number) {
  return apiFetch<NityaSevaEnrollmentDetailResponse>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments/${id}`,
  );
}

export async function fetchNityaSevaMonthSummary(params?: {
  status?: string;
  mode?: string;
  q?: string;
  limit?: number;
  offset?: number;
}) {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.mode) search.set('mode', params.mode);
  if (params?.q) search.set('q', params.q);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const suffix = search.toString() ? `?${search.toString()}` : '';
  return apiFetch<NityaSevaMonthSummary[]>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/months${suffix}`,
  );
}

export async function fetchNityaSevaMonthDetail(
  month: string,
  params?: {
    status?: string;
    mode?: string;
    q?: string;
    limit?: number;
    offset?: number;
  },
) {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.mode) search.set('mode', params.mode);
  if (params?.q) search.set('q', params.q);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const suffix = search.toString() ? `?${search.toString()}` : '';
  return apiFetch<NityaSevaMonthDetailResponse>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/months/${month}${suffix}`,
  );
}

export async function recordNityaSevaManualDonation(
  id: number,
  payload: { amount: number; paymentMethod?: string; date?: string; forMonth?: string; paymentFrequency?: NityaSevaPaymentFrequency; transactionId?: string; note?: string },
) {
  return apiFetch<{ donationId: number; enrollment: NityaSevaEnrollment }>(
    `${process.env.REACT_APP_BACKEND_API_URL}/api/admin/nityaseva/enrollments/${id}/manual-donation`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}
